/**
 * Shared reader for a metering denial (C-4.7).
 *
 * Extends the C-3.7 envelope in `src/lib/http/axios/denial.ts`. It does not
 * replace that envelope and it does not decide where a 403 navigates.
 * A body this reader does not recognise comes back null. A credits denial
 * whose reason is not one of the known values comes back `unparsed`.
 */

export const CREDIT_GATE_REASONS = [
    'monthly_cap_exceeded',
    'annual_pool_exceeded',
    'license_expired',
    'no_credits',
    'insufficient_for_action',
    'user_spend_limit_exceeded',
] as const;

export type CreditGateReason = (typeof CREDIT_GATE_REASONS)[number];

export interface CreditLimitDenial {
    kind: 'credit_limit';
    reason: CreditGateReason;
    message: string;
    resetDate?: string;
    licenseEndDate?: string | null;
    monthlyRemaining?: number;
    annualRemaining?: number;
    annualConsumed?: number;
    annualPool?: number;
    userSpendLimit?: number;
    userMonthToDate?: number;
}

export interface FreemiumLimitDenial {
    kind: 'freemium_limit';
    source: 'response' | 'session';
    message: string;
    currentCount?: number;
    limit?: number;
    limitType?: string;
    daysUntilReset?: number | null;
    limitMessage?: string | null;
}

export interface AccountLockedDenial {
    kind: 'account_locked';
    message: string;
}

/** A credits-shaped body whose reason is not one this reader knows. */
export interface UnparsedMeteringDenial {
    kind: 'unparsed';
    message: string;
    reason?: string;
}

export type MeteringDenial =
    | CreditLimitDenial
    | FreemiumLimitDenial
    | AccountLockedDenial
    | UnparsedMeteringDenial;

const CREDIT_LIMIT_ERROR = 'Credit limit reached';
const USAGE_LIMIT_ERROR = 'Usage limit reached';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function asString(value: unknown): string | undefined {
    return typeof value === 'string' ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
    return typeof value === 'number' ? value : undefined;
}

function isCreditGateReason(value: unknown): value is CreditGateReason {
    return typeof value === 'string' && (CREDIT_GATE_REASONS as readonly string[]).includes(value);
}

function creditLimitDenial(
    body: Record<string, unknown>,
    reason: CreditGateReason,
): CreditLimitDenial {
    const licenseEndDate = body.licenseEndDate;
    return {
        kind: 'credit_limit',
        reason,
        message: asString(body.message) ?? CREDIT_LIMIT_ERROR,
        ...(asString(body.resetDate) !== undefined ? { resetDate: asString(body.resetDate) } : {}),
        ...(licenseEndDate === null || typeof licenseEndDate === 'string'
            ? { licenseEndDate }
            : {}),
        ...(asNumber(body.monthlyRemaining) !== undefined
            ? { monthlyRemaining: asNumber(body.monthlyRemaining) }
            : {}),
        ...(asNumber(body.annualRemaining) !== undefined
            ? { annualRemaining: asNumber(body.annualRemaining) }
            : {}),
        ...(asNumber(body.annualConsumed) !== undefined
            ? { annualConsumed: asNumber(body.annualConsumed) }
            : {}),
        ...(asNumber(body.annualPool) !== undefined
            ? { annualPool: asNumber(body.annualPool) }
            : {}),
        ...(asNumber(body.userSpendLimit) !== undefined
            ? { userSpendLimit: asNumber(body.userSpendLimit) }
            : {}),
        ...(asNumber(body.userMonthToDate) !== undefined
            ? { userMonthToDate: asNumber(body.userMonthToDate) }
            : {}),
    };
}

function freemiumFrom(
    body: Record<string, unknown>,
    source: 'response' | 'session',
): FreemiumLimitDenial {
    const daysUntilReset = body.daysUntilReset;
    const limitMessage = body.limitMessage;
    return {
        kind: 'freemium_limit',
        source,
        message: asString(body.message) ?? asString(body.limitMessage) ?? USAGE_LIMIT_ERROR,
        ...(asNumber(body.currentCount) !== undefined
            ? { currentCount: asNumber(body.currentCount) }
            : {}),
        ...(asNumber(body.limit) !== undefined ? { limit: asNumber(body.limit) } : {}),
        ...(asString(body.limitType) !== undefined ? { limitType: asString(body.limitType) } : {}),
        ...(daysUntilReset === null || typeof daysUntilReset === 'number'
            ? { daysUntilReset }
            : {}),
        ...(limitMessage === null || typeof limitMessage === 'string' ? { limitMessage } : {}),
    };
}

/**
 * Read one metering denial from a response body or from `session.user.freemiumUsage`.
 * Claim denials (`NO_CLAIM`) and session expiry stay with the axios reader.
 */
export function readMeteringDenial(source: unknown): MeteringDenial | null {
    if (!isRecord(source)) return null;

    if (source.error === CREDIT_LIMIT_ERROR) {
        if (isCreditGateReason(source.reason)) return creditLimitDenial(source, source.reason);
        return {
            kind: 'unparsed',
            message: asString(source.message) ?? CREDIT_LIMIT_ERROR,
            ...(asString(source.reason) !== undefined ? { reason: asString(source.reason) } : {}),
        };
    }

    if (source.error === USAGE_LIMIT_ERROR) {
        return freemiumFrom(source, 'response');
    }

    if (source.reason === 'ACCOUNT_LOCKED') {
        return {
            kind: 'account_locked',
            message: asString(source.message) ?? 'Account locked',
        };
    }

    if (source.reason === 'INSUFFICIENT_CREDITS') {
        return creditLimitDenial(source, 'no_credits');
    }

    if (
        source.allowed === false &&
        (source.denial === 'no_credits' || source.denial === 'insufficient_for_action')
    ) {
        return creditLimitDenial(source, source.denial);
    }

    if (
        source.code === 'CREDITS_DENIED' &&
        typeof source.reason === 'string' &&
        source.reason !== 'SESSION_EXPIRED' &&
        source.reason !== 'NO_CLAIM'
    ) {
        return {
            kind: 'unparsed',
            message: asString(source.message) ?? 'Credit denial',
            reason: source.reason,
        };
    }

    if (
        source.hasReachedLimit === true &&
        (source.limitType === 'message' || source.limitType === 'token')
    ) {
        return freemiumFrom(source, 'session');
    }

    return null;
}
